import Papa from "papaparse";
import * as XLSX from "xlsx";

export const BULK_STUDENT_COLUMNS = [
    "rollNum",
    "name",
    "gender",
    "dateOfBirth",
    "sclassName",
    "guardianName",
    "guardianPhone",
    "guardianRelation",
    "guardianEmail",
    "disabilityType",
    "specialNeedsNotes",
];

export const REQUIRED_BULK_COLUMNS = ["rollNum", "name", "gender", "sclassName"];

export const OPTIONAL_BULK_COLUMNS = BULK_STUDENT_COLUMNS.filter(
    (c) => !REQUIRED_BULK_COLUMNS.includes(c)
);

/** Default student portal password after bulk upload (matches backend import). */
export function defaultPasswordForRoll(rollNum) {
    const n = String(rollNum).padStart(4, "0");
    return `Student${n}@`;
}

const FORM1_MALE_NAMES = [
    "James Phiri",
    "Thabo Mwale",
    "Blessings Namaja",
    "Herbert Pondamali",
    "Gracian Mlongoti",
    "Dalitso Kachale",
    "Yamikani Banda",
    "Chifundo Msiska",
    "Limbani Gondwe",
    "Kondwani Zimba",
    "Mphatso Chirwa",
    "Wongani Jere",
    "Pemphero Kamanga",
    "Chikondi Mhango",
    "Steven Chiume",
];

const FORM1_FEMALE_NAMES = [
    "Chisomo Banda",
    "Mercy Nyumba",
    "Grace Mwale",
    "Patience Banda",
    "Ruth Phiri",
    "Esther Kumwenda",
    "Mary Chirwa",
    "Linda Gondwe",
    "Agness Mhango",
    "Tamara Jere",
    "Wezi Kamanga",
    "Thokozani Zimba",
    "Chimwemwe Msiska",
    "Natasha Kachale",
    "Angela Chiume",
];

const GUARDIAN_FIRST = [
    "Grace",
    "Peter",
    "Mary",
    "John",
    "Agnes",
    "Paul",
    "Ruth",
    "David",
    "Esther",
    "Joseph",
];

/** Build sample rows for the downloadable bulk template (default: 30 Form 1 pupils). */
export function buildBulkStudentTemplateRows(count = 30) {
    const rows = [];
    for (let i = 1; i <= count; i++) {
        const isMale = i % 2 === 1;
        const name = isMale
            ? FORM1_MALE_NAMES[Math.floor((i - 1) / 2) % FORM1_MALE_NAMES.length]
            : FORM1_FEMALE_NAMES[Math.floor((i - 1) / 2) % FORM1_FEMALE_NAMES.length];
        const guardianFirst = GUARDIAN_FIRST[(i - 1) % GUARDIAN_FIRST.length];
        const surname = name.split(" ").slice(-1)[0] || "Banda";
        const month = String(((i - 1) % 12) + 1).padStart(2, "0");
        const day = String(((i * 3) % 27) + 1).padStart(2, "0");
        rows.push({
            rollNum: String(i),
            name,
            gender: isMale ? "Male" : "Female",
            dateOfBirth: `2012-${month}-${day}`,
            sclassName: "Form 1",
            guardianName: `${guardianFirst} ${surname}`,
            guardianPhone: `099${String(1000000 + i).slice(-7)}`,
            guardianRelation: i % 3 === 0 ? "Father" : i % 3 === 1 ? "Mother" : "Guardian",
            guardianEmail: i % 4 === 0 ? `guardian${i}@example.com` : "",
            disabilityType: i === 28 ? "Learning" : "",
            specialNeedsNotes: i === 28 ? "Needs front-row seating" : "",
        });
    }
    return rows;
}

export const BULK_STUDENT_TEMPLATE_ROWS = buildBulkStudentTemplateRows(30);

export function applyLockedClassToRows(rows, lockedClass) {
    if (!lockedClass || !Array.isArray(rows)) return rows;
    const id = String(lockedClass.id || lockedClass._id || "").trim();
    if (!id) return rows;
    return rows.map((row) => ({ ...row, sclassName: id }));
}

export function getClassDisplayName(cls) {
    if (!cls) return "";
    return String(cls.sclassName || cls.name || "").trim();
}

function resolveTemplateClassKey(classOptions = []) {
    const form1 = classOptions.find((c) =>
        /form\s*1/i.test(String(c.sclassName || c.name || "")),
    );
    const picked = form1 || classOptions[0];
    if (!picked) return "Form 1";
    return String(picked.id || picked._id || picked.sclassName || "Form 1");
}

const HEADER_ALIASES = {
    rollnum: "rollNum",
    studentid: "rollNum",
    "student id": "rollNum",
    fullname: "name",
    "full name": "name",
    studentname: "name",
    dob: "dateOfBirth",
    "date of birth": "dateOfBirth",
    birthdate: "dateOfBirth",
    classid: "sclassName",
    "class id": "sclassName",
    classname: "sclassName",
    class: "sclassName",
    guardianphone: "guardianPhone",
    "guardian phone": "guardianPhone",
    guardianname: "guardianName",
    "guardian name": "guardianName",
    guardianrelation: "guardianRelation",
    "guardian relation": "guardianRelation",
    guardianemail: "guardianEmail",
    "guardian email": "guardianEmail",
    disabilitytype: "disabilityType",
    "disability type": "disabilityType",
    specialneedsnotes: "specialNeedsNotes",
    "special needs notes": "specialNeedsNotes",
    sennotes: "specialNeedsNotes",
};

function normalizeHeader(h) {
    const raw = String(h || "").trim();
    const key = raw.toLowerCase().replace(/[_-]+/g, " ");
    return HEADER_ALIASES[key] || HEADER_ALIASES[raw.toLowerCase()] || raw;
}

const DISABILITY_ALIASES = {
    physical: "Physical",
    "physical disability": "Physical",
    visual: "Visual",
    "visual impairment": "Visual",
    blindness: "Visual",
    hearing: "Hearing",
    "hearing impairment": "Hearing",
    learning: "Learning",
    "learning disability": "Learning",
    behavioral: "Behavioral",
    behaviour: "Behavioral",
    multiple: "Multiple",
    other: "Other",
    none: "",
    "n/a": "",
};

function normalizeDisabilityType(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (DISABILITY_TYPES.has(raw)) return raw;
    const key = raw.toLowerCase().replace(/[_-]+/g, " ");
    if (DISABILITY_ALIASES[key]) return DISABILITY_ALIASES[key];
    for (const [alias, canonical] of Object.entries(DISABILITY_ALIASES)) {
        if (key.includes(alias) || alias.includes(key)) return canonical;
    }
    return raw;
}

function normalizeGender(value) {
    const s = String(value || "").trim().toLowerCase();
    if (s === "m" || s === "male" || s === "boy") return "Male";
    if (s === "f" || s === "female" || s === "girl") return "Female";
    return String(value || "").trim();
}

function normalizeRow(raw, rowIndex) {
    const mapped = { _rowIndex: rowIndex };
    for (const [key, value] of Object.entries(raw || {})) {
        const col = normalizeHeader(key);
        if (!col || col.startsWith("_")) continue;
        mapped[col] = value == null ? "" : String(value).trim();
    }
    if (mapped.gender) mapped.gender = normalizeGender(mapped.gender);
    if (mapped.disabilityType !== undefined) {
        mapped.disabilityType = normalizeDisabilityType(mapped.disabilityType);
    }
    if (mapped.rollNum !== undefined && mapped.rollNum !== "") {
        mapped.rollNum = String(mapped.rollNum).replace(/,/g, "").trim();
    }
    return mapped;
}

export function downloadCsvTemplate(classOptions = [], lockedClass = null) {
    const classKey = lockedClass
        ? String(lockedClass.id || lockedClass._id || "")
        : resolveTemplateClassKey(classOptions);
    const classLabel = getClassDisplayName(lockedClass) || "form1";
    const rows = buildBulkStudentTemplateRows(30).map((r) => ({
        ...r,
        sclassName: lockedClass ? getClassDisplayName(lockedClass) : classKey,
    }));
    const csv = Papa.unparse(rows, { columns: BULK_STUDENT_COLUMNS });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${classLabel.replace(/\s+/g, "_").toLowerCase()}_students_bulk_template_30.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export function parseCsvFile(file) {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: "greedy",
            transformHeader: normalizeHeader,
            complete: (results) => {
                const rows = (results.data || [])
                    .map((row, i) => normalizeRow(row, i))
                    .filter((row) => BULK_STUDENT_COLUMNS.some((c) => String(row[c] || "").trim()));
                if (results.errors?.length) {
                    const fatal = results.errors.find((e) => e.type === "Quotes" || e.type === "Delimiter");
                    if (fatal) {
                        reject(new Error(`CSV parse error on row ${fatal.row}: ${fatal.message}`));
                        return;
                    }
                }
                resolve(rows);
            },
            error: (err) => reject(err),
        });
    });
}

export function parseExcelFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                if (!sheetName) {
                    reject(new Error("The Excel file has no worksheets."));
                    return;
                }
                const sheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                const rows = json
                    .map((row, i) => normalizeRow(row, i))
                    .filter((row) => BULK_STUDENT_COLUMNS.some((c) => String(row[c] || "").trim()));
                resolve(rows);
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error("Could not read the Excel file."));
        reader.readAsArrayBuffer(file);
    });
}

export function suggestColumnMapping(headers = []) {
    const mapping = {};
    for (const header of headers) {
        const canonical = normalizeHeader(header);
        if (BULK_STUDENT_COLUMNS.includes(canonical) && !mapping[canonical]) {
            mapping[canonical] = header;
        }
    }
    return mapping;
}

export function mappingNeedsReview(mapping = {}, { skipClass = false } = {}) {
    const required = skipClass
        ? REQUIRED_BULK_COLUMNS.filter((c) => c !== "sclassName")
        : REQUIRED_BULK_COLUMNS;
    return required.some((col) => !mapping[col]);
}

export function applyColumnMapping(rawRows, mapping) {
    if (!mapping || !Object.keys(mapping).length) {
        return rawRows.map((row, i) => normalizeRow(row, i));
    }
    return rawRows
        .map((raw, rowIndex) => {
            const mapped = { _rowIndex: rowIndex, _sourceRow: rowIndex + 2 };
            for (const [targetField, sourceHeader] of Object.entries(mapping)) {
                if (!targetField || !sourceHeader) continue;
                const val = raw[sourceHeader];
                mapped[targetField] = val == null ? "" : String(val).trim();
            }
            if (mapped.gender) mapped.gender = normalizeGender(mapped.gender);
            if (mapped.disabilityType !== undefined) {
                mapped.disabilityType = normalizeDisabilityType(mapped.disabilityType);
            }
            if (mapped.rollNum !== undefined && mapped.rollNum !== "") {
                mapped.rollNum = String(mapped.rollNum).replace(/,/g, "").trim();
            }
            return mapped;
        })
        .filter((row) => BULK_STUDENT_COLUMNS.some((c) => String(row[c] || "").trim()));
}

function parseRawCsvFile(file) {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: "greedy",
            complete: (results) => {
                const headers = (results.meta?.fields || []).filter(Boolean);
                const rawRows = (results.data || []).filter((row) =>
                    Object.values(row || {}).some((v) => String(v ?? "").trim())
                );
                resolve({ headers, rawRows });
            },
            error: (err) => reject(err),
        });
    });
}

function parseRawExcelFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                if (!sheetName) {
                    reject(new Error("The Excel file has no worksheets."));
                    return;
                }
                const sheet = workbook.Sheets[sheetName];
                const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                const headers = rawRows.length
                    ? Object.keys(rawRows[0]).filter((h) => String(h || "").trim())
                    : [];
                resolve({
                    headers,
                    rawRows: rawRows.filter((row) =>
                        Object.values(row).some((v) => String(v ?? "").trim())
                    ),
                });
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error("Could not read the Excel file."));
        reader.readAsArrayBuffer(file);
    });
}

export async function parseRawUploadFile(file) {
    const name = String(file?.name || "").toLowerCase();
    if (name.endsWith(".csv")) return parseRawCsvFile(file);
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) return parseRawExcelFile(file);
    throw new Error("Please upload a .csv or .xlsx file.");
}

export async function parseStudentUploadFile(file, columnMapping = null) {
    const { headers, rawRows } = await parseRawUploadFile(file);
    if (columnMapping && Object.keys(columnMapping).length) {
        return applyColumnMapping(rawRows, columnMapping);
    }
    const suggested = suggestColumnMapping(headers);
    if (mappingNeedsReview(suggested)) {
        return applyColumnMapping(rawRows, suggested);
    }
    return rawRows.map((row, i) => normalizeRow(row, i)).filter((row) =>
        BULK_STUDENT_COLUMNS.some((c) => String(row[c] || "").trim())
    );
}

export async function parseStudentUploadFileLegacy(file) {
    const name = String(file?.name || "").toLowerCase();
    if (name.endsWith(".csv")) return parseCsvFile(file);
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) return parseExcelFile(file);
    throw new Error("Please upload a .csv or .xlsx file.");
}

const GENDERS = new Set(["Male", "Female"]);
const DISABILITY_TYPES = new Set([
    "",
    "Physical",
    "Visual",
    "Hearing",
    "Learning",
    "Behavioral",
    "Multiple",
    "Other",
]);

function normalizePhone(raw) {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("265") && digits.length >= 12) return `0${digits.slice(3, 12)}`;
    if (digits.length === 9) return `0${digits}`;
    if (digits.length === 10 && digits.startsWith("0")) return digits;
    return digits;
}

function isValidPhone(raw) {
    if (!String(raw || "").trim()) return true;
    const n = normalizePhone(raw);
    return /^0[789]\d{8}$/.test(n) || /^0[1-9]\d{7,8}$/.test(n);
}

function isValidEmail(raw) {
    const s = String(raw || "").trim();
    if (!s) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function isValidDate(raw) {
    if (!String(raw || "").trim()) return true;
    const d = new Date(raw);
    return !Number.isNaN(d.getTime());
}

function resolveClassId(value, classIndex) {
    const key = String(value || "").trim();
    if (!key) return null;
    if (classIndex.byId.has(key)) return classIndex.byId.get(key);
    const lower = key.toLowerCase();
    if (classIndex.byName.has(lower)) return classIndex.byName.get(lower);
    return null;
}

export function buildClassIndex(classes = []) {
    const byId = new Map();
    const byName = new Map();
    for (const c of classes) {
        const id = c.id || c._id;
        if (id) byId.set(String(id), c);
        const label = String(c.sclassName || "").trim().toLowerCase();
        if (label) byName.set(label, c);
    }
    return { byId, byName };
}

export function validateRowsLocally(rows, classIndex, lockedClass = null) {
    const lockedId = lockedClass ? String(lockedClass.id || lockedClass._id || "").trim() : "";
    const fileRollKeys = new Map();
    rows.forEach((row, idx) => {
        const rn = Number(row.rollNum);
        const cls = lockedClass || resolveClassId(String(row.sclassName || "").trim(), classIndex);
        const ck = cls?.id || cls?._id || lockedId || String(row.sclassName || "").trim();
        if (Number.isFinite(rn) && ck) fileRollKeys.set(`${ck}::${rn}`, idx);
    });

    return rows.map((row, rowIndex) => {
        const errors = [];
        const rollNum = Number(row.rollNum);
        const classKey = lockedId || String(row.sclassName || "").trim();
        const cls = lockedClass || resolveClassId(classKey, classIndex);

        if (!row.rollNum && row.rollNum !== 0) errors.push("Student ID is required");
        else if (!Number.isFinite(rollNum) || rollNum < 1) errors.push("Student ID must be a positive number");

        if (!String(row.name || "").trim()) errors.push("Full name is required");

        const gender = String(row.gender || "").trim();
        if (!gender) errors.push("Gender is required");
        else if (!GENDERS.has(gender)) errors.push('Use "Male" or "Female" for gender');

        if (!lockedId && !classKey) errors.push("Class ID is required");
        else if (!cls) errors.push("Class not found in your school");

        if (row.guardianPhone && !isValidPhone(row.guardianPhone)) {
            errors.push("Guardian phone looks invalid");
        }
        if (row.guardianEmail && !isValidEmail(row.guardianEmail)) {
            errors.push("Guardian email is invalid");
        }
        if (!isValidDate(row.dateOfBirth)) errors.push("Date of birth is invalid");
        if (row.disabilityType) row.disabilityType = normalizeDisabilityType(row.disabilityType);
        if (row.disabilityType && !DISABILITY_TYPES.has(String(row.disabilityType).trim())) {
            errors.push("Unknown disability type");
        }

        if (Number.isFinite(rollNum) && classKey) {
            const dupKey = cls ? `${cls.id || cls._id}::${rollNum}` : `${classKey}::${rollNum}`;
            if (fileRollKeys.get(dupKey) !== rowIndex) {
                errors.push("Duplicate ID in file for this class");
            }
        }

        return {
            ...row,
            _rowIndex: rowIndex,
            _errors: errors,
            _valid: errors.length === 0,
            _classLabel: cls?.sclassName || "",
        };
    });
}

export function chunkArray(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}
